import React, { useId } from 'react';
import { useIntl } from 'react-intl';
import { KitFontId, ReleaseKitLook, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { ThemeStillSong } from './useThemeStillSong';
import { themeMessages } from './themeMessages';
import { ThemeTiles } from './ThemeTiles';
import { ThemeLivePreview } from './ThemeLivePreview';
import { ThemeFontRow } from './ThemeFontRow';
import { ThemeColoursRow } from './ThemeColoursRow';
import { KitLogoField } from './KitLogoField';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Theme panel; each part uses its own slice of it */
import styles from './ThemePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ThemePanelBodyProps {
  accountId: number;
  bodyId: string;
  artistName: string;
  look: ReleaseKitLook;
  palette: readonly string[];
  /** The colours of a swatch edit. With `onChangePalette` the Colours row shows (the song page only). */
  editablePalette?: readonly string[];
  hasPaletteOverride?: boolean;
  onChangePalette?: (palette: string[]) => void;
  onResetPalette?: () => void;
  song: ThemeStillSong;
  logoUrl: string | null;
  saveAsDefault: boolean;
  isSaving: boolean;
  onPickTheme: (themeId: ReleaseThemeId) => void;
  onPickFont: (fontOverride: KitFontId | null) => void;
  onPickLogo: (logoFileId: number | null, logoUrl: string | null) => void;
  onToggleDefault: () => void;
  /** The Save and Cancel bar of the song page. */
  footer?: React.ReactNode;
}

/**
 * The open Theme panel (design q2, q2b, q4): 8 theme tiles that play their sample loops, a still
 * of the user's own song in the theme, the five colours (the song page), "Change font" (closed by
 * default), the logo and "Save as my default". Below 700 px the still goes first and the tiles scroll sideways (q4).
 */
const ThemePanelBodyComponent: React.FC<ThemePanelBodyProps> = ({
  accountId,
  bodyId,
  artistName,
  look,
  palette,
  editablePalette,
  hasPaletteOverride = false,
  onChangePalette,
  onResetPalette,
  song,
  logoUrl,
  saveAsDefault,
  isSaving,
  onPickTheme,
  onPickFont,
  onPickLogo,
  onToggleDefault,
  footer,
}) => {
  const intl = useIntl();
  const saveDefaultId = useId();
  return (
    <div id={bodyId} className={styles.body}>
      <div className={styles.picker}>
        <div className={styles.tilesBlock}>
          <ThemeTiles value={look.themeId} onChange={onPickTheme} />
          <span className={styles.note}>
            <span className={styles.wideOnly}>{intl.formatMessage(themeMessages.samplesNote)}</span>
            <span className={styles.narrowOnly}>{intl.formatMessage(themeMessages.samplesNoteNarrow)}</span>
          </span>
        </div>
        <ThemeLivePreview themeId={look.themeId} fontOverride={look.fontOverride} palette={palette} song={song} />
      </div>
      <div className={styles.rows}>
        {onChangePalette !== undefined && onResetPalette !== undefined && editablePalette !== undefined && (
          <ThemeColoursRow
            themeId={look.themeId}
            palette={editablePalette}
            isOwn={hasPaletteOverride}
            disabled={isSaving}
            onChange={onChangePalette}
            onReset={onResetPalette}
          />
        )}
        <ThemeFontRow themeId={look.themeId} fontOverride={look.fontOverride} disabled={false} onChange={onPickFont} />
        <KitLogoField accountId={accountId} logoUrl={logoUrl} disabled={isSaving} onChange={onPickLogo} />
      </div>
      <label className={styles.check} htmlFor={saveDefaultId}>
        <input
          id={saveDefaultId}
          type="checkbox"
          className={styles.checkBox}
          checked={saveAsDefault}
          disabled={isSaving}
          onChange={onToggleDefault}
        />
        <span>
          <b className={styles.checkTitle}>{intl.formatMessage(themeMessages.saveDefault)}</b>
          <br />
          {intl.formatMessage(themeMessages.saveDefaultHint, { artist: artistName })}
        </span>
      </label>
      {footer}
    </div>
  );
};

export const ThemePanelBody = TypedMemo(ThemePanelBodyComponent);
